// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper, { SectionWrapperStyles } from "@flip/App/Components/Common/SectionWrapper";
import XngCarousel, { SwiperStyle } from "@flip/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { SLIDES_PER_VIEW_POINTS } from "@flip/XDL/constants";
import { colorText, generateSlidesPerViewMap, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
    subtitle = "subtitle",
    subtitleUrl = "subtitleUrl",
    showAuthorizedOnly = "showAuthorizedOnly",
    displayAsCarouselOnMobile = "displayAsCarouselOnMobile",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
        [fields.subtitle]: string;
        [fields.subtitleUrl]: LinkFieldValue;
        [fields.showAuthorizedOnly]: boolean;
        [fields.displayAsCarouselOnMobile]: boolean;
    };
}
// XNG-Changes: FLIP-1044
const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize:
            props.fields.brandsToDisplay === "select-brands"
                ? parseInt("100", 10)
                : parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
        // XNG-Changes: FLIP-867
        authorizedSupplier: props.fields.showAuthorizedOnly === true,
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        // XNG-Changes
        isMobileScreen: state.xCommon.isMobileScreen,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // XNG-Changes
    swiperStyles?: SwiperStyle;
    brandsMobileWrapper?: InjectableCss;
    sectionWrapperStyles?: SectionWrapperStyles;
    mobileBrandsWrapper?: InjectableCss;
}

// XNG-Changes: styles
export const listStyles: BrandListStyles = {
    container: {
        css: css`
            margin-bottom: 33px;
        `,
    },
    swiperStyles: {
        container: {
            css: css``,
        },
    },
    titleText: {
        variant: "h2",
        css: css`
            margin-bottom: 48px;
        `,
    },
    imageItem: {
        css: css`
            margin: auto;
            display: flex;
            justify-content: center;
            width: 100%;
            height: 100%;
            max-width: 204px;
            max-height: 204px;
            ${xScreen("lte", "md")} {
                max-width: 232px;
                max-height: 232px;
            }
            ${xScreen("lte", 767)} {
                max-width: 144px;
                max-height: 144px;
            }
            ${xScreen("lte", "xs")} {
                max-width: 104px;
                max-height: 104px;
            }
        `,
    },
    image: {
        css: css`
            img {
                object-fit: contain;
                height: auto;
                max-height: 97px;
                width: 150px;
                margin: auto;

                // XNG-Change: had to use important to override inline style
                ${xScreen("lte", "md")} {
                    max-height: 97px !important;
                    width: 150px !important;
                }

                ${xScreen("lte", 767)} {
                    width: 140px !important;
                }

                ${xScreen("lte", "xs")} {
                    max-height: 104px !important;
                    width: 104px !important;
                }
            }
        `,
    },
    brandsMobileWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
        `,
    },
    sectionWrapperStyles: {
        wrapper: {
            css: css`
                & > div {
                    ${xScreen("lte", 600)} {
                        display: flex;
                        flex-direction: column;
                    }
                }
            `,
        },
        title: {
            link: {
                css: css`
                    ${xScreen("lte", 600)} {
                        margin: 20px 0 0;
                    }
                `,
            },
        },
    },
    mobileBrandsWrapper: {
        css: css`
            display: flex;
            column-gap: 8px;
            row-gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
            & > div {
                margin: 0;
                width: calc((100% - 16px) / 3);
                max-width: calc((100% - 16px) / 3);
            }
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    // XNG-Changes: FLIP-835, added isMobileScreen to include only mobiles without Ipad Mini
    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
            isMobileScreen,
            isTabletScreen,
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };

        const breakpoints = generateSlidesPerViewMap({
            [SLIDES_PER_VIEW_POINTS.xs]: 3,
            [SLIDES_PER_VIEW_POINTS.sm]: 3,
            [SLIDES_PER_VIEW_POINTS.md]: 4,
            [SLIDES_PER_VIEW_POINTS.lg]: 6,
        });

        const brandsList = brandList.map(brand => (
            <StyledWrapper key={brand.id} {...styles.imageItem}>
                {brand === brandList[0] && (
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to full brand list")}
                        focusFunction={focusFunction}
                    />
                )}
                <Clickable href={brand.detailPagePath} {...styles.link} aria-label={translate("Go to brand page")}>
                    <LazyImage
                        imgProps={imgProps}
                        {...styles.image}
                        src={brand.logoSmallImagePath}
                        altText={`${translate("Go to brand page")} ${brand.logoAltText || brand.name}`}
                    />
                </Clickable>
            </StyledWrapper>
        ));
        // XNG-Changes: FLIP-835, added isSmallMobileScreen to include only mobiles without Ipad Mini
        return (
            <SectionWrapper
                title={colorText(fields.title)}
                linkText={fields.subtitle}
                link={fields.subtitleUrl}
                data-test-selector="brandGallery"
                extendedStyles={styles.sectionWrapperStyles}
            >
                <VisuallyHidden as="h2">{translate("Explore available brands")}</VisuallyHidden>
                {isMobileScreen && !fields.displayAsCarouselOnMobile ? (
                    <StyledWrapper {...styles.mobileBrandsWrapper}>{brandsList}</StyledWrapper>
                ) : (
                    <XngCarousel
                        spaceBetween={isMobileScreen ? 8 : 22}
                        breakpoints={{
                            0: { slidesPerView: 3 },
                            ...breakpoints,
                        }}
                        loop={true}
                    >
                        {brandsList}
                    </XngCarousel>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.showAuthorizedOnly,
                displayName: "Show Authorized Manufacturers",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
            },
            // XNG-Changes: FLIP-933
            // {
            //     name: fields.maximumNumberOfBrandsPerRow,
            //     displayName: "Maximum brands per row",
            //     editorTemplate: "DropDownField",
            //     defaultValue: "6",
            //     options: [
            //         { displayName: "1", value: "1" },
            //         { displayName: "2", value: "2" },
            //         { displayName: "3", value: "3" },
            //         { displayName: "4", value: "4" },
            //         { displayName: "5", value: "5" },
            //         { displayName: "6", value: "6" },
            //     ],
            //     fieldType: "Contextual",
            //     tooltip:
            //         "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
            //     tooltipPosition: "left",
            // },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
            {
                name: fields.subtitle,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.subtitleUrl,
                displayName: "Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                isVisible: item => item.fields.subtitle,
            },
            {
                name: fields.displayAsCarouselOnMobile,
                displayName: "Display as carousel on mobile",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
